
import { useEffect, useRef, useCallback } from "react";
import { useSceneStore } from "../stores/sceneStore";
import { useSoundtrackStore } from "../stores/soundtrackStore";
import { API_BASE } from "@/lib/apiClient";

function resolveUrl(url: string): string {
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  return `${API_BASE}${url}`;
}

/** Multi-track soundtrack playback hook */
export function useSoundtrack() {
  const audioMapRef = useRef<Map<number, HTMLAudioElement>>(new Map());
  const fadeTimersRef = useRef<Map<number, ReturnType<typeof setInterval>>>(
    new Map(),
  );

  const scene = useSceneStore((s) => s.scene);
  const soundtracks = useSoundtrackStore((s) => s.soundtracks);
  const enabledTrackIndices = useSoundtrackStore((s) => s.enabledTrackIndices);
  const isPlaying = useSoundtrackStore((s) => s.isPlaying);
  const volume = useSoundtrackStore((s) => s.volume);
  const isMuted = useSoundtrackStore((s) => s.isMuted);

  const setSoundtracks = useSoundtrackStore((s) => s.setSoundtracks);
  const togglePlay = useSoundtrackStore((s) => s.toggleSoundtrackPlay);
  const setVolume = useSoundtrackStore((s) => s.setSoundtrackVolume);
  const toggleMute = useSoundtrackStore((s) => s.toggleSoundtrackMute);
  const toggleTrackEnabled = useSoundtrackStore((s) => s.toggleTrackEnabled);

  useEffect(() => {
    const tracks = scene.soundtracks ?? [];
    const currentTracks = useSoundtrackStore.getState().soundtracks;

    const sameTracks =
      tracks.length === currentTracks.length &&
      tracks.every((t, i) => t.trackId === currentTracks[i]?.trackId);

    if (!sameTracks) {
      const audioMap = audioMapRef.current;
      for (const [, audio] of audioMap) {
        audio.pause();
        audio.src = "";
      }
      audioMap.clear();
      for (const [, timer] of fadeTimersRef.current) {
        clearInterval(timer);
      }
      fadeTimersRef.current.clear();
    }

    setSoundtracks(tracks);
  }, [scene.soundtracks, setSoundtracks]);

  useEffect(() => {
    const audioMap = audioMapRef.current;
    const enabledSet = new Set(enabledTrackIndices);

    for (const [index, audio] of audioMap) {
      if (!enabledSet.has(index)) {
        audio.pause();
        audio.src = "";
        audioMap.delete(index);
        const timer = fadeTimersRef.current.get(index);
        if (timer) {
          clearInterval(timer);
          fadeTimersRef.current.delete(index);
        }
      }
    }

    for (const index of enabledTrackIndices) {
      if (!audioMap.has(index) && soundtracks[index]) {
        const track = soundtracks[index];
        const audio = new Audio();
        audio.loop = track.loop !== false;
        audio.preload = "auto";
        audio.src = resolveUrl(track.url);
        audioMap.set(index, audio);
      }
    }
  }, [enabledTrackIndices, soundtracks]);

  useEffect(() => {
    const audioMap = audioMapRef.current;
    const masterVol = isMuted ? 0 : volume;

    for (const [index, audio] of audioMap) {
      const track = soundtracks[index];
      if (!track) continue;

      const trackVol = (track.volume ?? 1) * masterVol;

      if (isPlaying) {
        const fadeIn = track.fadeIn ?? 0;
        if (fadeIn > 0 && audio.paused) {
          const stepMs = 50;
          const steps = Math.max(1, (fadeIn * 1000) / stepMs);
          const increment = trackVol / steps;
          let current = 0;
          audio.volume = 0;
          audio.play().catch(() => {});

          const existingTimer = fadeTimersRef.current.get(index);
          if (existingTimer) clearInterval(existingTimer);

          const timer = setInterval(() => {
            current = Math.min(trackVol, current + increment);
            const a = audioMapRef.current.get(index);
            if (a) a.volume = current;
            if (current >= trackVol) {
              clearInterval(timer);
              fadeTimersRef.current.delete(index);
            }
          }, stepMs);
          fadeTimersRef.current.set(index, timer);
        } else {
          audio.volume = trackVol;
          audio.play().catch(() => {});
        }
      } else {
        const timer = fadeTimersRef.current.get(index);
        if (timer) {
          clearInterval(timer);
          fadeTimersRef.current.delete(index);
        }
        audio.pause();
      }
    }
  }, [isPlaying, isMuted, volume, soundtracks, enabledTrackIndices]);

  useEffect(() => {
    const audioMap = audioMapRef.current;
    const fadeTimers = fadeTimersRef.current;
    return () => {
      for (const [, audio] of audioMap) {
        audio.pause();
        audio.src = "";
      }
      audioMap.clear();
      for (const [, timer] of fadeTimers) {
        clearInterval(timer);
      }
      fadeTimers.clear();
    };
  }, []);

  const handleTogglePlay = useCallback(() => {
    if (enabledTrackIndices.length === 0) return;
    togglePlay();
  }, [enabledTrackIndices.length, togglePlay]);

  return {
    soundtracks,
    enabledTrackIndices,
    isPlaying,
    volume,
    isMuted,
    hasTracks: soundtracks.length > 0,
    togglePlay: handleTogglePlay,
    setVolume,
    toggleMute,
    toggleTrack: toggleTrackEnabled,
  };
}
